import { GraphQLQuery } from "@aws-amplify/api";
import { Box, Button, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { IconAlertTriangle, IconBug } from "@tabler/icons-react";
import { API, graphqlOperation } from "aws-amplify";

import { useAuthContext } from "../../hooks/auth";
import { handleError } from "../../utils/error";

// Invalid GraphQL query that will cause an error
const invalidCampaignInquiriesQuery = /* GraphQL */ `
    query InvalidCampaignInquiries {
        campaignInquiries(nonExistentParam: "test") {
            items {
                id
                nonExistentField
            }
        }
    }
`;

// Another invalid GraphQL query with a different error
const invalidCampaignSearchQuery = /* GraphQL */ `
    query InvalidCampaignSearch {
        searchCampaigns(invalidFilter: { term: "test" }) {
            items {
                id
            }
        }
    }
`;

// A third invalid GraphQL query
const malformedGraphQLQuery = /* GraphQL */ `
    query MalformedQuery {
        campaignInquiry(campaignInquiryId: "test" badSyntax) {
            id
        }
    }
`;

export const DeveloperToolsPage = () => {
    const { user } = useAuthContext();

    // Only allow access to the developer tools page for specific email
    if (user?.email !== "matej.klobusnik@qest.cz") {
        return (
            <Box p="xl">
                <Paper p="xl" withBorder>
                    <Stack align="center" spacing="md">
                        <IconAlertTriangle size={48} color="red" />
                        <Title order={3}>Access Denied</Title>
                        <Text>You don't have permission to access this page.</Text>
                        <Text size="sm">Current user: {user?.email}</Text>
                        <Text size="sm">Required user: matej.klobusnik@qest.cz</Text>
                    </Stack>
                </Paper>
            </Box>
        );
    }

    const triggerUnhandledException = () => {
        // This will throw an unhandled exception that bubbles up to the global handler
        const obj = null;
        obj.nonExistentMethod(); // Will throw TypeError: Cannot read properties of null
    };

    const triggerGraphQLError1 = async () => {
        try {
            // Make invalid GraphQL request that will fail
            await API.graphql<GraphQLQuery<unknown>>(
                graphqlOperation(invalidCampaignInquiriesQuery),
            );
        } catch (error) {
            handleError(error, { context: { requestId: "123" } });
        }
    };

    const triggerGraphQLError2 = async () => {
        try {
            // Make invalid GraphQL request that will fail with different error
            await API.graphql<GraphQLQuery<unknown>>(
                graphqlOperation(invalidCampaignSearchQuery),
            );
        } catch (error) {
            handleError(error, { context: { requestId: "123" } });
        }
    };

    const triggerGraphQLError3 = async () => {
        try {
            // Make GraphQL request with syntax error
            await API.graphql<GraphQLQuery<unknown>>(
                graphqlOperation(malformedGraphQLQuery),
            );
        } catch (error) {
            handleError(error, { context: { requestId: "123" } });
        }
    };

    const triggerTypeError = () => {
        try {
            // Create real type error by accessing property of undefined
            const userObj = undefined;
            // We know this will cause an error, but it's intentional for testing
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - Intentionally causing a type error
            userObj.profile.username;
        } catch (error) {
            handleError(error, { context: { requestId: "123" } });
        }
    };

    const triggerFormValidationError = () => {
        try {
            // Simulate form validation error with real validation context
            const formValues = {
                email: "invalid-email",
                password: "123", // Too short
            };

            // Validation function that will throw an error
            function validateForm(values: Record<string, unknown>) {
                const errors: Record<string, string> = {};

                if (typeof values.email === "string" && !values.email.includes("@")) {
                    errors.email = "Invalid email format";
                }

                if (typeof values.password === "string" && values.password.length < 8) {
                    errors.password = "Password must be at least 8 characters";
                }

                if (Object.keys(errors).length > 0) {
                    const validationError = new Error("Form validation failed");
                    // @ts-expect-error - Adding custom property
                    validationError.validationErrors = errors;
                    throw validationError;
                }
            }

            validateForm(formValues);
        } catch (error) {
            // Get validation errors from the error object if available
            const validationContext = error instanceof Error &&
                // @ts-expect-error - Accessing custom property
                error.validationErrors ?
                // @ts-expect-error - Accessing custom property
                { validationErrors: error.validationErrors } :
                {};

            handleError(error, { context: { requestId: "123" } });
        }
    };

    const triggerImageFetchingError = () => {
        // Create actual error by trying to load non-existent image
        const img = new Image();

        img.onerror = () => {
            const error = new Error("Failed to load image");
            handleError(error, { context: { imageUrl: img.src } });
        };

        // Set source to non-existent image to trigger error
        img.src = "https://nonexistent-domain-123456789.com/image.jpg";
    };

    const triggerNetworkError = async () => {
        try {
            // Actual network error by fetching from non-existent endpoint
            await fetch("https://non-existent-api-endpoint.testujto.com/api/v1/data");
        } catch (error) {
            handleError(error, { context: { requestId: "123" } });
        }
    };

    const triggerReactRenderError = () => {
        try {
            // React errors generally happen during render
            // We'll simulate by creating an invalid component setup

            // This would normally happen in a component's render method
            // Here we deliberately create an error similar to one that would occur
            const items = null;
            // This will fail because items is null and we can't map it
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - Intentional null error for testing
            items.map(item => item.name);
        } catch (error) {
            handleError(error, { additionalMessage: "Cannot read properties of null (reading 'map')" });
        }
    };

    return (
        <Box p="xl">
            <Paper p="xl" withBorder mb="xl">
                <Stack spacing="md">
                    <Group position="apart">
                        <Title order={2}>
                            <IconBug style={{ verticalAlign: "middle", marginRight: 8 }} />
                            Developer Tools - Error Testing
                        </Title>
                        <Text>User: {user?.email}</Text>
                    </Group>
                    <Text color="dimmed">
                        This page contains buttons to test various error handling scenarios.
                        All errors except the unhandled exception will be caught and reported to Sentry
                        using the handleError utility.
                    </Text>
                </Stack>
            </Paper>

            <Stack spacing="md">
                <Group grow>
                    <Button color="red" onClick={triggerUnhandledException}>
                        Test Unhandled Exception
                    </Button>
                    <Button color="orange" onClick={triggerGraphQLError1}>
                        Test GraphQL Error 1
                    </Button>
                </Group>

                <Group grow>
                    <Button color="orange" onClick={triggerGraphQLError2}>
                        Test GraphQL Error 2
                    </Button>
                    <Button color="orange" onClick={triggerGraphQLError3}>
                        Test GraphQL Error 3
                    </Button>
                </Group>

                <Group grow>
                    <Button color="yellow" onClick={triggerTypeError}>
                        Test Type Error
                    </Button>
                    <Button color="yellow" onClick={triggerFormValidationError}>
                        Test Form Validation Error
                    </Button>
                </Group>

                <Group grow>
                    <Button color="blue" onClick={triggerImageFetchingError}>
                        Test Image Fetching Error
                    </Button>
                    <Button color="blue" onClick={triggerNetworkError}>
                        Test Network Error
                    </Button>
                </Group>

                <Group grow>
                    <Button color="violet" onClick={triggerReactRenderError}>
                        Test React Render Error
                    </Button>
                </Group>
            </Stack>
        </Box>
    );
};